import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to monitor network connection status and quality
 * 
 * @returns {Object} Network status information
 *   - isOnline: boolean - Is device online
 *   - isOffline: boolean - Is device offline
 *   - effectiveType: string - Connection effective type (4g, 3g, 2g, slow-2g)
 *   - downlink: number - Estimated downlink speed in Mbps
 *   - rtt: number - Estimated round-trip time in ms
 *   - saveData: boolean - Is data saver mode enabled
 *   - connectionType: string - Connection type (wifi, cellular, etc)
 */
export default function useNetworkStatus() {
  const [status, setStatus] = useState({
    isOnline: navigator.onLine,
    isOffline: !navigator.onLine,
    effectiveType: null,
    downlink: null,
    rtt: null,
    saveData: false,
    connectionType: null,
  });

  const updateStatus = useCallback(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      setStatus({
        isOnline: navigator.onLine,
        isOffline: !navigator.onLine,
        effectiveType: connection.effectiveType || null,
        downlink: connection.downlink || null,
        rtt: connection.rtt || null,
        saveData: connection.saveData || false,
        connectionType: connection.type || null,
      });
    } else {
      setStatus({
        isOnline: navigator.onLine,
        isOffline: !navigator.onLine,
        effectiveType: null,
        downlink: null,
        rtt: null,
        saveData: false,
        connectionType: null,
      });
    }
  }, []);

  useEffect(() => {
    updateStatus();

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    const handleOnline = () => updateStatus();
    const handleOffline = () => updateStatus();
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    if (connection) {
      connection.addEventListener('change', updateStatus);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', updateStatus);
      }
    };
  }, [updateStatus]);

  const isSlowConnection = status.effectiveType === '2g' || status.effectiveType === 'slow-2g';
  const isFastConnection = status.effectiveType === '4g';

  return {
    ...status,
    isSlowConnection,
    isFastConnection,
  };
}


